import { Link, useLocation } from "react-router-dom";
import { Home, Menu, X, Globe } from "lucide-react";
import { useState, useEffect } from "react";
import { useLanguage } from "@/lib/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="main-navbar"
      className={`floating-nav transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] ${
        scrolled || !isHome
          ? "crystal-glass-strong rounded-full px-3 py-2"
          : "bg-transparent px-3 py-2 rounded-full"
      }`}
    >
      <div className="flex items-center justify-between w-full">
        {/* Logo */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2 px-4 py-1"
        >
          <Home className="w-5 h-5 text-[#1D1D1F]" />
          <span className="font-semibold text-[#1D1D1F] tracking-tight text-lg hidden sm:inline" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Oona Flats
          </span>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            data-testid="nav-home-link"
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              location.pathname === "/"
                ? "bg-black text-white"
                : "text-[#1D1D1F] hover:bg-black/5"
            }`}
          >
            {t("nav_home")}
          </Link>
          <Link
            to="/unterkuenfte"
            data-testid="nav-listings-link"
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              location.pathname === "/unterkuenfte"
                ? "bg-black text-white"
                : "text-[#1D1D1F] hover:bg-black/5"
            }`}
          >
            {t("nav_listings")}
          </Link>
          <Link
            to="/kontakt"
            data-testid="nav-contact-link"
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              location.pathname === "/kontakt"
                ? "bg-black text-white"
                : "text-[#1D1D1F] hover:bg-black/5"
            }`}
          >
            {t("nav_contact")}
          </Link>
          <Link
            to="/empfehlungen"
            data-testid="nav-recommendations-link"
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              location.pathname === "/empfehlungen"
                ? "bg-black text-white"
                : "text-[#1D1D1F] hover:bg-black/5"
            }`}
          >
            {t("nav_tips")}
          </Link>

          {/* Language Switcher */}
          <button
            data-testid="language-switcher"
            onClick={toggleLang}
            className="ml-2 flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-[#1D1D1F] hover:bg-black/5 transition-all border border-black/10"
            title={lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="uppercase text-xs font-bold tracking-wider">{lang === "de" ? "EN" : "DE"}</span>
          </button>
        </div>

        {/* Mobile: Lang + Menu */}
        <div className="md:hidden flex items-center gap-2">
          <button
            data-testid="language-switcher-mobile"
            onClick={toggleLang}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold text-[#1D1D1F] border border-black/10 hover:bg-black/5 transition-colors"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === "de" ? "EN" : "DE"}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="p-2 rounded-full hover:bg-black/5 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 mt-2 crystal-glass-strong rounded-2xl p-4 flex flex-col gap-2">
          <Link to="/" data-testid="mobile-nav-home" onClick={() => setMenuOpen(false)}
            className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 transition-colors">
            {t("nav_home")}
          </Link>
          <Link to="/unterkuenfte" data-testid="mobile-nav-listings" onClick={() => setMenuOpen(false)}
            className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 transition-colors">
            {t("nav_listings")}
          </Link>
          <Link to="/kontakt" data-testid="mobile-nav-contact" onClick={() => setMenuOpen(false)}
            className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 transition-colors">
            {t("nav_contact")}
          </Link>
          <Link to="/empfehlungen" data-testid="mobile-nav-recommendations" onClick={() => setMenuOpen(false)}
            className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 transition-colors">
            {t("nav_tips")}
          </Link>
        </div>
      )}
    </nav>
  );
}
